import React, { useCallback, useMemo } from 'react';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import Card from 'components/shared/Card';
import CopyrightMotion from '../CopyrightMotion';
import { TechnologyConflict } from 'store/types/Meeting';
import { ActivityType, IPCheckType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { meetingSessionToActivity } from 'util/meeting';

interface CopyrightReleaseStatusCardProps {
  item: TechnologyConflict;
  index: number;
  updateItem: (id: string, patch: Partial<TechnologyConflict>) => void;
  displayConflict: (conflict: TechnologyConflict) => void;
}

const CopyrightReleaseStatusCard: React.FunctionComponent<CopyrightReleaseStatusCardProps> = ({
  item,
  index,
  updateItem,
  displayConflict,
}) => {
  const { meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();

  const itemNumber = index + 1;

  const enableSave = useMemo(() => item.hasLoa, [item.hasLoa]);
  const displayedItem = useMemo(
    () => ({
      ...item,
      itemNumber,
    }),
    [item, itemNumber]
  );

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        motion: meetingSession?.currentMotion,
        votingSession: meetingSession?.currentVotingSession,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          copyrightConflict: QuestionResponse.Yes,
        },
        technologyConflict: displayedItem,
      },
      'Saved Copyrighted Item'
    );
  }, [displayedItem, logActivity, meetingSession]);

  return (
    <Card
      title={<Typography variant="h6">Copyright release Status of copyrighted item #{itemNumber}</Typography>}
      bordered
    >
      <GridContainer>
        <GridItem>
          <FormControl>
            <FormLabel>Has the copyright release been received from its owner?.</FormLabel>
            <RadioGroup
              name="is-used-radio-group"
              value={item.hasLoa ?? ''}
              onChange={(event) => {
                const value = event.target.value as QuestionResponse;
                if (value === item.hasLoa) return;

                const patch = { hasLoa: value };
                const nextItem = { ...displayedItem, ...patch };
                updateItem(item.id, patch);
                void displayConflict(nextItem);
              }}
            >
              <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
              <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
            </RadioGroup>
          </FormControl>
        </GridItem>
        {item.hasLoa === QuestionResponse.Yes && (
          <GridItem>
            <Typography>
              PROCEED to assess NEXT one, or if this is the last one, GO TO Assessment of disclosed (identified)
              trademark
            </Typography>
          </GridItem>
        )}
        {item.hasLoa === QuestionResponse.No && (
          <GridItem>
            <CopyrightMotion />
          </GridItem>
        )}
        <GridItem>
          <Button variant="contained" color="secondary" disabled={!enableSave} onClick={handleSave}>
            Save
          </Button>
        </GridItem>
      </GridContainer>
    </Card>
  );
};

export default CopyrightReleaseStatusCard;
